import { postPreExchangeOperation } from "../../../app/api/exchangeOffer";
import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { determinePassengerType } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookedTrips, IBookingResponse, IDeterminePassenger, IPreExchangeOperationRequest, IPreExchangeOperationResponse, ISelectedPassenger } from "../../../Orchestrator/Types/Index";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";
import { extractCarriedOverAddons } from "./extractCarriedOverAddons";

export interface IUpgradeToPremiumContinueParams {
    trip?: IBookedTrips;
    bookingId?: string;
    upgradeOutboundOffers?: any;
    upgradeInboundOffers?: any;
    selectedPassengers?: string[];
    selectedTrips?: { outbound: boolean; inbound: boolean };
    outboundPassengers?: string[];
    inboundPassengers?: string[];
}

class UpgradeToPremiumContinueClickAction extends BaseAction<string, IUpgradeToPremiumContinueParams> {
    // Coalesces accidental double-fires (e.g. button double-click). Safe here
    // because every concurrent dispatch comes from the same Continue click on
    // /bookings/upgrade-premium with identical params. If a future caller
    // dispatches concurrently with DIFFERENT params, the second caller's
    // params would be silently ignored — convert to per-call instances
    // (`new UpgradeToPremiumContinueClickAction()`) before that lands.
    protected override deduplicate = true;

    private handleDirectCheckout = async (
        upgradeOutboundOffers: any,
        upgradeInboundOffers: any,
        bookingId: string,
        selectedPassengers: string[],
        selectedTrips: { outbound: boolean; inbound: boolean },
        outboundPassengers?: string[],
        inboundPassengers?: string[]
    ): Promise<boolean> => {
        try {
            // Collect exchange offers and candidate passengers from selected trips
            let allExchangeOffers: any[] = [];
            let candidatePassengers: any[] = [];
            if (selectedTrips.outbound && upgradeOutboundOffers?.exchangeOffers?.length > 0) {
                allExchangeOffers = [...allExchangeOffers, ...upgradeOutboundOffers.exchangeOffers];
                if (Array.isArray(upgradeOutboundOffers?.passengers)) {
                    candidatePassengers = [...candidatePassengers, ...upgradeOutboundOffers.passengers];
                }
            }

            if (selectedTrips.inbound && upgradeInboundOffers?.exchangeOffers?.length > 0) {
                allExchangeOffers = [...allExchangeOffers, ...upgradeInboundOffers.exchangeOffers];
                if (Array.isArray(upgradeInboundOffers?.passengers)) {
                    candidatePassengers = [...candidatePassengers, ...upgradeInboundOffers.passengers];
                }
            }

            if (allExchangeOffers.length === 0) return false;

            // Set exchange offers atom with all offer IDs
            const offerIds = Array.from(new Set(allExchangeOffers.flatMap((offer: any) => {
                const ids = offer?.offerId;
                if (Array.isArray(ids)) {
                    return ids;
                }
                return ids ? [ids] : [];
            })));
            updateRecoilState<string[]>("exchangeOffersAtom", offerIds);

            const exchangeOffers: IPreExchangeOperationRequest['exchangeOffers'] = allExchangeOffers.flatMap((offer: any) => {
                const offerIdsForEntry = Array.isArray(offer?.offerId) ? offer.offerId : [offer.offerId];
                return offerIdsForEntry.filter((id: string | undefined): id is string => Boolean(id)).map((singleOfferId: string) => {
                    let passengersToUse: string[] = selectedPassengers;
                    const isOutboundOffer = upgradeOutboundOffers?.exchangeOffers?.some((outboundOffer: any) => Array.isArray(outboundOffer.offerId) ? outboundOffer.offerId.includes(singleOfferId) : outboundOffer.offerId === singleOfferId);
                    const isInboundOffer = upgradeInboundOffers?.exchangeOffers?.some((inboundOffer: any) => Array.isArray(inboundOffer.offerId) ? inboundOffer.offerId.includes(singleOfferId) : inboundOffer.offerId === singleOfferId);

                    if (isOutboundOffer && outboundPassengers) {
                        passengersToUse = outboundPassengers;
                    } else if (isInboundOffer && inboundPassengers) {
                        passengersToUse = inboundPassengers;
                    }

                    const passengerExternalRefs: string[] = Array.from(new Set(
                        passengersToUse
                            .map((selectedId: string) =>
                                candidatePassengers.find((p: any) => p.id === selectedId)?.externalRef
                            )
                            .filter((ref: any): ref is string => Boolean(ref))
                    ));

                    return {
                        offerId: singleOfferId,
                        placeSelections: null,
                        passengerRefs: passengerExternalRefs,
                        throughTicketTags: null
                    };
                });
            });

            const payloadPreExchangeOperation: IPreExchangeOperationRequest = {
                exchangeOffers
            };

            const preExchangeResponse: any = await postPreExchangeOperation(payloadPreExchangeOperation, bookingId);

            if (preExchangeResponse.status === 'ok' && preExchangeResponse.answer) {
                const bookingResponse: IPreExchangeOperationResponse = preExchangeResponse.answer;
                if (bookingResponse.exchangeOperation) {
                    sessionStorage.setItem("exchangeOperationId", bookingResponse.exchangeOperation.id)
                    sessionStorage.setItem("exchangeBookingId", bookingId)
                    //Get Booking by Booking Id
                    const getBookingResponse: any = await getBooking(bookingId);
                    if (getBookingResponse.status === 'ok' && getBookingResponse.answer) {
                        const bookingResponse: IBookingResponse = getBookingResponse.answer;
                        // Update Recoil state with the booking response
                        updateRecoilState('quickExchangeBookingResponseAtom', bookingResponse);
                        updateRecoilState('bookingResponseAtom', bookingResponse);

                        // Surface existing booking ancillaries (now EXCHANGE_ONGOING on the
                        // pre-exchange offers) as read-only "carried over" addons so the
                        // upgrade checkout shows them without letting the user edit/repurchase.
                        updateRecoilState("carriedOverAddonsAtom", extractCarriedOverAddons(bookingResponse.booking));

                        const passengerList: ISelectedPassenger[] = bookingResponse.booking?.passengers.map(passenger => {
                            const passengerObject: ISelectedPassenger = {
                                id: passenger.id,
                                firstName: passenger?.detail?.firstName || "",
                                lastName: passenger?.detail?.lastName || "",
                                email: passenger?.detail?.email || "",
                                mobile: "",
                                remember: false,
                                age: passenger.age,
                                seatingDetails: null,
                                cards: passenger.cards || [],
                                prmNeeds: passenger.prmNeeds
                            }
                            return passengerObject
                        });
                        let filteredPassengers: ISelectedPassenger[] = [];
                        if (selectedPassengers.length > 0) {
                            filteredPassengers = passengerList.filter(p => selectedPassengers.includes(p.id));
                        }

                        const passengersObject: { [key: string]: number } = {};
                        filteredPassengers.forEach((passenger) => {
                            const determinePassenger: IDeterminePassenger = {
                                age: passenger.age || 0,
                                cards: passenger?.cards || [],
                                prmNeeds: ((passenger?.prmNeeds || []).length > 0 && passenger?.prmNeeds) ? passenger?.prmNeeds[0] : ""
                            }
                            const passengerType = determinePassengerType(determinePassenger);

                            // Initialize the count for the passenger type if not already present
                            if (!passengersObject[passengerType]) {
                                passengersObject[passengerType] = 0;
                            }

                            // Increment the count for this passenger type
                            passengersObject[passengerType] += 1;
                        });

                        updateRecoilState("searchStateAtom", { passengers: passengersObject });


                        const filteredBookingPassengers = bookingResponse.booking?.passengers.filter(p => selectedPassengers.includes(p.id)) || [];
                        updateRecoilState("passengerStateAtom", null)
                        updateRecoilState("passengerStateAtom", filteredBookingPassengers);


                        const addonsCreditSummary = getRecoilStateValue("changeTripAlreadyPaidAddonsAtom")                        
                        // INVARIANT: an upgrade summary build MUST pass a
                        // selectedPassengerForCancel / selectedPassengerForCancelInBound
                        // array (at least one of the two conditional spreads below must
                        // fire). PrepareBookingSummaryAction only runs its
                        // preparePaymentSummary(isUpgrade=true) pass inside those
                        // sub-branches, and that pass is what persists
                        // `upgradeBaseFareTotal`. If both were ever omitted, that field
                        // is never set and mergeUpgradePaymentSummary falls back to its
                        // fragile `total - addons - seats` derivation (the upgrade addon
                        // total-drift bug).
                        prepareBookingSummaryAction.setParams({
                            ...bookingResponse.booking,
                            changeTripAlreadyPaidAddons: addonsCreditSummary || {},
                            isUpgrade: true,
                            ...(selectedTrips.outbound && upgradeOutboundOffers?.exchangeOffers?.length > 0
                                ? { selectedPassengerForCancel: outboundPassengers }
                                : {}),
                            ...(selectedTrips.inbound && upgradeInboundOffers?.exchangeOffers?.length > 0
                                ? { selectedPassengerForCancelInBound: inboundPassengers }
                                : {})
                        })
                        await prepareBookingSummaryAction.execute()

                        if (upgradeOutboundOffers?.exchangeOffers?.length > 0 && selectedTrips.outbound) {
                            updateRecoilState("selectedPassengerForCancelAtom", outboundPassengers);
                        } if (upgradeInboundOffers?.exchangeOffers?.length > 0 && selectedTrips.inbound) {
                            updateRecoilState("selectedPassengerForCancelInBoundAtom", inboundPassengers);
                        }
                        // const params: ICalculatePassengerListByPriceActionParams = {
                        //     booking: bookingResponse.booking,
                        //     screen: "",
                        //     passengerSpecificationsTypes: null
                        // }
                        // calculatePassengerListByPriceAction.setParams(params)
                        // await calculatePassengerListByPriceAction.execute();

                        updateRecoilState("paymentStateAtom", {
                            // Left unset so <Payment> can preselect the customer's default saved
                            // card — it only preselects while no method is set (Payment.tsx).
                            method: null,
                            total: 0,
                        });

                        updateRecoilState("isChangeDateStateAtom", true);
                        updateRecoilState("isQuickExchangeStateAtom", true)
                        updateRecoilState("isUpgradeToPremiumStateAtom", true);
                        return true;
                    }
                }
            }
            return false;
        } catch (error) {
            console.error("handleDirectCheckout error:", error);
            return false;
        }
    };

    override async run(): Promise<string> {
        let response: string = "fail"
        try {
            localStorage.removeItem(STORAGE_KEYS.CHANGE_DATE_FLOW);
            localStorage.removeItem("isUpgradeToPremiumFlow");
            const {
                trip,
                upgradeOutboundOffers,
                upgradeInboundOffers,
                bookingId,
                selectedPassengers = [],
                selectedTrips = { outbound: false, inbound: false },
                outboundPassengers,
                inboundPassengers
            } = this.params!;

            if (bookingId && (selectedTrips.outbound || selectedTrips.inbound) && selectedPassengers.length > 0) {
                const outboundOffersToPass = selectedTrips.outbound ? upgradeOutboundOffers : null;
                const inboundOffersToPass = selectedTrips.inbound ? upgradeInboundOffers : null;

                // Always call direct checkout with selected data
                const ok = await this.handleDirectCheckout(
                    outboundOffersToPass,
                    inboundOffersToPass,
                    bookingId,
                    selectedPassengers,
                    selectedTrips,
                    outboundPassengers,
                    inboundPassengers
                );
                if (ok) {
                    response = "success";
                    localStorage.setItem(STORAGE_KEYS.CHANGE_DATE_FLOW, "true");
                    localStorage.setItem("isUpgradeToPremiumFlow", "true");
                } else {
                    response = "fail";
                    return response;
                }
            } else {
                console.warn("Missing required data for upgrade premium continue action");
                response = "fail";
                return response;
            }
        } catch (error) {
            console.error("UpgradeToPremiumContinueClickAction error:", error);
        }
        return response;
    }
}

export const upgradeToPremiumContinueClickAction = new UpgradeToPremiumContinueClickAction();
